import {Button, Dialog} from 'platform/components';

import {useState} from 'react';

import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {queryParams, usePermissions} from '@dms/shared';

import {composePath, useNavigate, useRequiredParams} from 'shared/web';

import {SelectVehicleDialogDataGrid} from './BusinessCaseOffersSelectVehicleDatagrid';

export type SelectVehicleFullScreenProps = {
  onClose: () => void;
  isOpen: boolean;
  onSubmit: (invoiceId: string) => void;
};

export const BusinessCaseOffersSelectVehicleDialog = ({
  onClose,
  isOpen,
  onSubmit,
}: SelectVehicleFullScreenProps) => {
  const navigate = useNavigate();
  const {id: businessCaseId} = useRequiredParams();

  const [selectedId, setSelectedId] = useState<string>();

  const [canCreateBusinessCaseSelling] = usePermissions({
    permissionKeys: ['createBusinessCaseSelling'],
  });

  const handleSubmit = () => {
    if (selectedId) {
      onSubmit(selectedId);
    }
  };

  const handleCreateSaleVehicle = () => {
    onClose();
    navigate(
      composePath(vehiclesRoutes.createV2, {
        queryParams: {
          [queryParams.REDIRECT_SOURCE]: EntityResourceIds.businessCase,
          [queryParams.RETURN_URL]: composePath(businessCaseRoutes.selling, {
            params: {id: businessCaseId},
          }),
          id: businessCaseId,
        },
      })
    );
  };

  return (
    <Dialog
      data-testid={testIds.businessCase.detail('addSaleVehicle')}
      size="large"
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t('page.businessCase.labels.selectVehicle')}
      // It is a product requirement to have a button here (in the dialog header, next to
      // the close button), but we do not want to extend or update the platform Dialog
      // component because of one case. We also will not rename the existing property nor
      // add a new one because of this one case. So we use the `pagination` slot
      // as an escape hatch to satisfy the product requirement.
      pagination={
        <Button
          title={i18n.t('entity.vehicle.labels.createVehicle')}
          onClick={handleCreateSaleVehicle}
          isDisabled={!canCreateBusinessCaseSelling}
          data-testid={testIds.businessCase.detail('createSaleVehicle')}
        />
      }
      buttons={[
        {
          title: i18n.t('general.actions.cancel'),
          variant: 'secondary',
          onClick: onClose,
        },

        {
          title: i18n.t('general.labels.select'),
          variant: 'primary',
          onClick: handleSubmit,
          isDisabled: !selectedId,
        },
      ]}
    >
      <SelectVehicleDialogDataGrid
        data-testid={testIds.businessCase.detail('addSaleVehicle')}
        onRowSelectionChange={(rows) => {
          setSelectedId(rows[0]?.id);
        }}
      />
    </Dialog>
  );
};
